import { Box, Container, Paper, Stack, Typography } from '@mui/material';
import React, { RefObject, useRef } from 'react';

const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
};

const paragraphStyle = {
    display: 'block',
    marginTop: '10px',
    marginBottom: '10px',
};

type TermsOfPaymentProps = {
    contentRef?: RefObject<HTMLDivElement>;
};

const TermsOfPayment = ({ contentRef }: TermsOfPaymentProps) => {
    const internalRef = useRef<HTMLDivElement>(null);
    const printableRef = contentRef ?? internalRef;

    return (
        <Container maxWidth='lg' sx={{ mt: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, md: 5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                }}
            >
                <Stack spacing={3} ref={printableRef}>
                    <Box>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Effective: September 20, 2023
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            1. Host Payments Terms
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            If you are a Host with an active 7DAY account, you
                            are entitled to receive the amount successfully
                            collected from the Guest for completed Experiences,
                            approved Add-ons and Partner Services provided
                            during the Reservation.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            For the purposes of these Terms:
                        </Typography>
                        <Stack
                            component='ul'
                            spacing={1}
                            sx={{ ...paragraphStyle, pl: 3 }}
                        >
                            <Typography variant='body3' component='li'>
                                "Add-ons" are additional Experiences, products
                                or services that are owned, managed and hosted
                                directly by the Host and added to a Guest
                                reservation.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                "Partner Services" are products or services
                                provided by a third-party contractor, supplier
                                or service provider and arranged in connection
                                with a Guest reservation.
                            </Typography>
                        </Stack>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Payment to Hosts will only be processed once the
                            Guest has paid their invoice in full.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Payouts are calculated based on the total Experience
                            reservation amount, Add-ons, Expense Claims and
                            Partner Services completed by the end of the
                            previous calendar month. The applicable Host Service
                            Fee is deducted from the original confirmed
                            Reservation amount at the time of Reservation. The
                            Service Fee does not apply to additional Add-ons,
                            Expense Claims and Partner Services incurred during
                            the Guest’s stay unless otherwise stated.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Hosts may submit additional Add-ons, Expense Claims
                            or Partner Services that were not recorded during
                            the Guest’s Experience period after the Reservation
                            has concluded. Any such charges will be included in
                            a subsequent invoice to the Guest. Payment to the
                            Host for these additional Add-ons will only be
                            processed once the corresponding funds have been
                            successfully collected from the Guest.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            To receive payment, your account must be fully set
                            up with all required billing and payout details.
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            2. Guest Billing and Payment Terms
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            When you make a Reservation through 7DAY:
                        </Typography>
                        <Stack component='ul' sx={paragraphStyle}>
                            <Typography variant='body3' component='li'>
                                You agree to pay the total amount for the
                                reservation, including the base Experience price
                                and any Add-ons, Expense Claims or Partner
                                Services.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                A Guest Service Fee applies at the time of
                                Reservation, which will be added ontop of the
                                total Experience reservation amount.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                You remain responsible for full payment of all
                                amounts invoiced, including additional services
                                incurred during the Experience.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Payment obligations remain with the account
                                holder.
                            </Typography>
                        </Stack>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Failure to meet payment obligations may result in
                            account restrictions or Reservation cancellations.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            3. Guest Monthly Invoicing
                        </Typography>
                        <Stack component='ul' spacing={1} sx={{ pl: 3 }}>
                            <Typography variant='body3' component='li'>
                                Invoices are issued on the 1st of each month.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Each invoice includes completed Experiences,
                                Add-ons, Expense Claims and Partner Services
                                from the previous calendar month.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Guests have 30 days from the invoice date to
                                make full payment.
                            </Typography>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            4. Guest Late Payment & Account Holds
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            If payment is not received within 30 days:
                        </Typography>
                        <Stack component='ul' sx={paragraphStyle}>
                            <Typography variant='body3' component='li'>
                                Your account will be placed on hold.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                You will be unable to make new Reservations.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Upcoming reservations will be automatically
                                cancelled 5 days after the 30-day payment period
                                ends.
                            </Typography>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            5. Discounts and Pricing Adjustments
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Hosts may also apply a discount to an existing
                            Reservation after it has been created, provided the
                            Reservation status is Pending, Approved, or
                            Completed. Discounts cannot be applied to
                            Reservations that have been Declined or Cancelled,
                            nor can they be applied once the applicable Guest
                            invoice has been issued as part of the billing
                            cycle.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Where a discount is applied to an existing
                            Reservation, the discount will apply only to the
                            Experience Reservation amount. Discounts do not
                            apply to Add-ons, Expense Claims, Partner Services,
                            Service Fees, taxes, or any other charges unless
                            expressly stated otherwise.
                        </Typography>

                        <Typography variant='body3' sx={paragraphStyle}>
                            Any change made by a Host to the standard pricing,
                            rates, fees, AI pricing settings, or seasonal rates
                            of an Experience will apply only to future
                            Reservations made after the change takes effect.
                            Such changes will not alter the pricing of any
                            existing Reservation unless a specific Reservation
                            discount is applied in accordance with these Terms.
                        </Typography>

                        <Typography variant='body3' sx={paragraphStyle}>
                            7DAY reserves the right to prevent, reverse, or
                            reject any pricing adjustment or discount that is
                            applied in error, breaches these Terms, or results
                            in an incorrect Reservation value.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            6. Service Fees
                        </Typography>
                        <Stack component='ul' sx={paragraphStyle}>
                            <Typography variant='body3' component='li'>
                                A Host Service Fee is deducted from the total
                                confirmed Reservation amount at the time the
                                Reservation is made. This fee does not apply to
                                additional Add-ons, Expense Claims or Partner
                                Services incurred during the Guest’s Experience.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                A Guest Service Fee applies at the time of
                                Reservation and is payable by the Guest.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Service Fees are subject to change at any time.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Guests remain responsible for the full
                                Reservation value and any additional approved
                                charges as invoiced.
                            </Typography>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            7. Guest Failed or Declined Payments
                        </Typography>
                        <Stack component='ul' sx={paragraphStyle}>
                            <Typography variant='body3' component='li'>
                                Payment issues must be resolved promptly.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                7DAY may retry payment or require an alternative
                                method.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Continued non-payment may result in Reservation
                                cancellation or account suspension.
                            </Typography>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            8. Cancellations & Outstanding Amounts
                        </Typography>
                        <Stack component='ul' sx={paragraphStyle}>
                            <Typography variant='body3' component='li'>
                                Cancelling your account does not remove your
                                obligation to pay outstanding invoices.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Pre-paid amounts are non-refundable unless
                                otherwise stated.
                            </Typography>
                            <Typography variant='body3' component='li'>
                                Termination does not affect fees owed up to the
                                termination date.
                            </Typography>
                        </Stack>
                    </Box>
                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            9. Contractors
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            For the purposes of these Terms, Contractor services
                            are classified as Partner Services, being products
                            or services provided by a third-party contractor,
                            supplier, or service provider in connection with an
                            Experience.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Contractors engaged in connection with an Experience
                            are not paid by 7DAY. 7DAY is not responsible for
                            invoicing Hosts for contractor services or making
                            payments directly to Contractors.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Where a Contractor service is added to a
                            Reservation, the associated cost may be included as
                            part of the Guest's overall Reservation. However,
                            the Host remains solely responsible for
                            coordinating, managing, and paying the Contractor
                            directly in accordance with the pricing, payment
                            arrangements, and terms agreed between the Host and
                            Contractor.
                        </Typography>

                        <Typography variant='body3' sx={paragraphStyle}>
                            7DAY does not process, hold, distribute, or
                            guarantee payments to Contractors and accepts no
                            responsibility or liability for any contractor
                            payment arrangements.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Any contractor-specific terms and conditions,
                            including those relating to cancellations, refunds,
                            fees, service delivery, availability, or other
                            service policies, are the responsibility of the
                            Contractor and Host to establish, communicate,
                            manage, and enforce directly. Any disputes relating
                            to payments, cancellations, services provided,
                            pricing, refunds, or other contractor-related
                            matters must be resolved directly between the
                            Contractor and the Host.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            If a Contractor updates their pricing templates,
                            rates, or service inclusions, those changes will
                            apply only to new Reservations made after the
                            updated pricing becomes effective. Existing
                            Reservations will retain the pricing, fees, and
                            inclusions that were applicable at the time the
                            Reservation was confirmed, unless otherwise agreed
                            in writing between the Host and Contractor.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant='body3' sx={paragraphStyle}>
                            By creating an account or making a Reservation, you
                            acknowledge and agree to these payment terms.
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        </Container>
    );
};
export default TermsOfPayment;
